// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  Receipt, CreditCard, Settings, Search, Plus, Edit, Trash2, Eye, Loader2, ChevronDown,
  Clock, DollarSign, AlertTriangle, CheckCircle, XCircle, RefreshCw, Link2, TrendingUp, Download, Info,
  Banknote, Ban
} from 'lucide-react';
import api from '@/lib/api';
import { exportToCSV } from '@/components/crm/csv-export';
import { appPath } from '@/lib/basePath';
import { CRM_BASE } from './types';
import type { RemodulateTarget } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
// NB: qui ClientDetailData = risposta di /payments/client/:tenantId (storico
// pagamenti), NON il /clients/:id/full-detail usato da TabClienti.
import type {
  CrmMasterInstallment as Installment,
  CrmMasterClientPayments as ClientDetailData,
  CrmMasterCronRun as CronRun,
  CrmMasterPaymentsDashboard as DashboardData,
  CrmMasterMrrPoint as MrrPoint,
  CrmMasterInstallment as FailedPayment,
  CrmMasterUpcomingCharge as UpcomingCharge,
  CrmMasterSyncErrorsReport as SyncErrorsReport,
  CrmMasterWebhookTest as WebhookTestResult,
  CrmMasterCronRun as CronLogEntry,
} from '@easyflow/shared';
import {
  Tip, Badge, ModalWrapper, AdvFilters, EMPTY_ADV_FILTERS, countAdvFilters,
  FilteredBadge, advFiltersToParams, AdvancedFilters
} from './shared';

// Task #3875 — AgingReport ora arriva dal contratto condiviso.
import type { CrmMasterAgingReport as AgingReport } from '@easyflow/shared';

export function TabPagamenti() {
  const { toast } = useToast();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [mrrData, setMrrData] = useState<MrrPoint[]>([]);
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [clientDetail, setClientDetail] = useState<ClientDetailData | null>(null);
  const [failedPayments, setFailedPayments] = useState<FailedPayment[]>([]);
  const [showRemodulate, setShowRemodulate] = useState<RemodulateTarget | null>(null);
  const [remodPlan, setRemodPlan] = useState<Array<{ installment_number: number; amount: number; due_date: string; notes: string }>>([]);
  const [remodSaving, setRemodSaving] = useState(false);
  const [cronHistory, setCronHistory] = useState<CronRun[]>([]);
  const [upcomingCharges, setUpcomingCharges] = useState<UpcomingCharge[]>([]);
  const [syncErrors, setSyncErrors] = useState<SyncErrorsReport | null>(null);
  const [showMonitoring, setShowMonitoring] = useState(false);
  const [monitoringTab, setMonitoringTab] = useState<'cron' | 'upcoming' | 'errors' | 'webhook'>('cron');
  const [webhookTest, setWebhookTest] = useState<WebhookTestResult | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [cronLogs, setCronLogs] = useState<CronLogEntry[]>([]);
  const [cronLogsLoading, setCronLogsLoading] = useState(false);
  const [chargingId, setChargingId] = useState<string | null>(null);
  const [manualPayModalPg, setManualPayModalPg] = useState<{ id: string; number: number; amount: number } | null>(null);
  const [manualPayMethodPg, setManualPayMethodPg] = useState<'bank_transfer' | 'cash' | 'cheque'>('bank_transfer');
  const [manualPayRefPg, setManualPayRefPg] = useState('');
  const [manualPayNotesPg, setManualPayNotesPg] = useState('');
  const [manualPayLoadingPg, setManualPayLoadingPg] = useState(false);
  const [advFilters, setAdvFilters] = useState<AdvFilters>(EMPTY_ADV_FILTERS);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const instParams = advFiltersToParams(advFilters, { from: 'from_date', to: 'to_date' });
      if (statusFilter) instParams.set('status', statusFilter);
      const instQs = instParams.toString();
      const dashQs = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' }).toString();
      const results = await Promise.allSettled([
        api.get<DashboardData>(`${CRM_BASE}/payments/dashboard${dashQs ? `?${dashQs}` : ''}`),
        api.get<Installment[] | { installments?: Installment[] }>(`${CRM_BASE}/payments/installments${instQs ? `?${instQs}` : ''}`),
        api.get<MrrPoint[] | { months?: MrrPoint[] }>(`${CRM_BASE}/payments/mrr`),
        api.get<AgingReport>(`${CRM_BASE}/payments/aging`),
        api.get<FailedPayment[]>(`${CRM_BASE}/payments/failed`),
      ]);
      const [dashRes, instRes, mrrRes, agingRes, failedRes] = results;
      if (dashRes.status === 'fulfilled' && dashRes.value?.success && dashRes.value.data) setDashboard(dashRes.value.data);
      if (instRes.status === 'fulfilled' && instRes.value?.success && instRes.value.data) setInstallments(Array.isArray(instRes.value.data) ? instRes.value.data : (instRes.value.data).installments || []);
      if (mrrRes.status === 'fulfilled' && mrrRes.value?.success && mrrRes.value.data) setMrrData(Array.isArray(mrrRes.value.data) ? mrrRes.value.data : (mrrRes.value.data).months || []);
      if (agingRes.status === 'fulfilled' && agingRes.value?.success && agingRes.value.data) setAging(agingRes.value.data);
      if (failedRes.status === 'fulfilled' && failedRes.value?.success && failedRes.value.data) setFailedPayments(Array.isArray(failedRes.value.data) ? failedRes.value.data : []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, advFilters]);

  useEffect(() => { void loadData(); }, [loadData]);

  const loadMonitoring = async () => {
    try {
      const results = await Promise.allSettled([
        api.get<CronRun[]>(`${CRM_BASE}/cron-jobs/history?limit=20`),
        api.get<UpcomingCharge[]>(`${CRM_BASE}/cron-jobs/upcoming?limit=15`),
        api.get<SyncErrorsReport>(`${CRM_BASE}/sync-errors`),
      ]);
      const [cronRes, upRes, errRes] = results;
      if (cronRes.status === 'fulfilled' && cronRes.value?.success && cronRes.value.data) setCronHistory(Array.isArray(cronRes.value.data) ? cronRes.value.data : []);
      if (upRes.status === 'fulfilled' && upRes.value?.success && upRes.value.data) setUpcomingCharges(Array.isArray(upRes.value.data) ? upRes.value.data : []);
      if (errRes.status === 'fulfilled' && errRes.value?.success && errRes.value.data) setSyncErrors(errRes.value.data);
    } catch (e) { console.error('Monitoring load error:', e); }
  };

  useEffect(() => {
    if (showMonitoring) void loadMonitoring();
  }, [showMonitoring]);

  const loadWebhookTest = async () => {
    setWebhookLoading(true);
    try {
      const res = await api.get<WebhookTestResult>(`${CRM_BASE}/payments/webhook-test`);
      if (res.success && res.data) setWebhookTest(res.data);
    } catch (e) { console.error('Webhook test error:', e); }
    finally { setWebhookLoading(false); }
  };

  const loadCronLogs = async () => {
    setCronLogsLoading(true);
    try {
      const res = await api.get(`${CRM_BASE}/payments/cron-logs?limit=20&job_type=stripe_installment_charge`);
      if (res.success && res.data) setCronLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Cron logs error:', e); }
    finally { setCronLogsLoading(false); }
  };

  useEffect(() => {
    if (showMonitoring && monitoringTab === 'webhook') void loadWebhookTest();
    if (showMonitoring && monitoringTab === 'cron') void loadCronLogs();
  }, [showMonitoring, monitoringTab]);

  const chargeInstallment = async (installmentId: string) => {
    if (!confirm('Confermi l\'addebito manuale immediato di questa rata?')) return;
    setChargingId(installmentId);
    try {
      const res = await api.post<{ message?: string }>(`${CRM_BASE}/payments/charge-installment/${installmentId}`);
      if (res.success) {
        toast({ title: 'Addebito avviato', description: res.data?.message || 'Addebito in corso' });
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore addebito', variant: 'destructive' });
      }
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore addebito', description: (__e?.response?.data as { error?: string } | undefined)?.error || __e.message || 'Errore', variant: 'destructive' });
    } finally { setChargingId(null); }
  };

  const handleManualPaymentPg = async () => {
    if (!manualPayModalPg) return;
    setManualPayLoadingPg(true);
    try {
      const res = await api.post<{ message?: string; warnings?: string[] }>(`${CRM_BASE}/installments/${manualPayModalPg.id}/mark-paid-manual`, {
        payment_method: manualPayMethodPg,
        reference: manualPayRefPg.trim() || undefined,
        notes: manualPayNotesPg.trim() || undefined,
      });
      if (res.success) {
        const desc = res.data?.warnings?.length
          ? `${res.data.message}. Avvisi: ${res.data.warnings.join('; ')}`
          : res.data?.message || 'Pagamento manuale registrato con successo';
        toast({ title: 'Pagamento registrato', description: desc, variant: res.data?.warnings?.length ? 'destructive' : undefined });
        setManualPayModalPg(null);
        setManualPayMethodPg('bank_transfer');
        setManualPayRefPg('');
        setManualPayNotesPg('');
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore sconosciuto', variant: 'destructive' });
      }
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore pagamento manuale', description: (__e?.response?.data as { error?: string } | undefined)?.error || __e.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setManualPayLoadingPg(false);
    }
  };

  const viewClient = async (tenantId: string) => {
    const res = await api.get<ClientDetailData>(`${CRM_BASE}/payments/client/${tenantId}`);
    if (res.success && res.data) setClientDetail(res.data);
  };

  const retryPayment = async (id: string) => {
    await api.post(`${CRM_BASE}/installments/${id}/retry`);
    void loadData();
  };

  const [globalSuspendModal, setGlobalSuspendModal] = useState<{ contractId: string; contractNumber?: string } | null>(null);
  const [globalSuspendReason, setGlobalSuspendReason] = useState('');
  const [globalSuspendLoading, setGlobalSuspendLoading] = useState(false);

  const suspendPayments = (contractId: string, contractNumber?: string) => {
    setGlobalSuspendReason('');
    setGlobalSuspendModal({ contractId, contractNumber });
  };

  const handleGlobalSuspend = async () => {
    if (!globalSuspendModal || !globalSuspendReason.trim()) return;
    setGlobalSuspendLoading(true);
    try {
      await api.post(`${CRM_BASE}/contracts/${globalSuspendModal.contractId}/suspend-payments`, { reason: globalSuspendReason.trim(), actor: 'admin' });
      setGlobalSuspendModal(null);
      setGlobalSuspendReason('');
      void loadData();
      toast({ title: 'Pagamenti sospesi', description: 'I pagamenti sono stati sospesi con successo.' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore', description: (__e?.response?.data as { error?: string } | undefined)?.error || 'Impossibile sospendere', variant: 'destructive' });
    } finally {
      setGlobalSuspendLoading(false);
    }
  };

  const resumePayments = async (contractId: string) => {
    if (!confirm('Riprendere i pagamenti per questo contratto? Le scadenze verranno ricalcolate.')) return;
    try {
      await api.post(`${CRM_BASE}/contracts/${contractId}/resume-payments`, { actor: 'admin', reason: 'Ripresa manuale' });
      void loadData();
      toast({ title: 'Pagamenti ripresi', description: 'I pagamenti sono stati ripresi con successo.' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore', description: (__e?.response?.data as { error?: string } | undefined)?.error || 'Impossibile riprendere', variant: 'destructive' });
    }
  };

  const openRemodulate = (inst: Installment) => {
    const samePlan = (i: Installment): boolean => {
      if (inst.contract_id) return i.contract_id === inst.contract_id;
      if (inst.invoice_id) return i.invoice_id === inst.invoice_id;
      if (inst.pipeline_entry_id) {
        return !i.contract_id && !i.invoice_id && i.pipeline_entry_id === inst.pipeline_entry_id;
      }
      return false;
    };
    const existingPlan = installments
      .filter((i) => samePlan(i) && i.status !== 'paid' && !i.is_entry_fee)
      // Task #3875 — normalizzazione wire→draft: numeric pg può arrivare come stringa.
      .map((i) => ({ installment_number: i.installment_number ?? 0, amount: Number(i.amount ?? 0), due_date: String(i.due_date ?? '').split('T')[0] || '', notes: i.notes || '' }));
    setRemodPlan(existingPlan.length > 0 ? existingPlan : [{ installment_number: 1, amount: 0, due_date: '', notes: '' }]);
    setShowRemodulate(inst);
  };

  const saveRemodulation = async () => {
    if (!showRemodulate) return;
    setRemodSaving(true);
    try {
      await api.put(`${CRM_BASE}/invoices/${showRemodulate.invoice_id}/remodulate`, { installments: remodPlan });
      setShowRemodulate(null);
      void loadData();
    } finally {
      setRemodSaving(false);
    }
  };

  const cancelInstallment = async (installmentId: string) => {
    if (!confirm('Annullare questa rata futura non pagata? L\'operazione non è reversibile.')) return;
    setCancellingId(installmentId);
    try {
      const res = await api.post<{ message?: string }>(`${CRM_BASE}/payments/installments/${installmentId}/cancel`);
      if (res.success) {
        toast({ title: 'Rata annullata', description: res.data?.message || 'La rata è stata annullata.' });
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Impossibile annullare la rata', variant: 'destructive' });
      }
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore', description: (__e?.response?.data as { error?: string } | undefined)?.error || __e?.message || 'Errore', variant: 'destructive' });
    } finally {
      setCancellingId(null);
    }
  };

  const updateRemodInst = (index: number, field: string, value: string | number) => {
    const EMPTY_REMOD = { installment_number: 0, amount: 0, due_date: '', notes: '' };
    setRemodPlan(prev => {
      const updated = [...prev];
      updated[index] = { ...(updated[index] ?? EMPTY_REMOD), [field]: field === 'amount' ? parseFloat(String(value)) || 0 : value };
      return updated;
    });
  };

  const recalculateRemaining = (editedIndex: number) => {
    setRemodPlan(prev => {
      if (!showRemodulate || prev.length <= 1) return prev;
      const invoice = installments.find((i) => i.invoice_id === showRemodulate.invoice_id);
      const paidTotal = installments
        .filter((i) => i.invoice_id === showRemodulate.invoice_id && i.status === 'paid')
        .reduce((s: number, i) => s + (parseFloat(String(i.amount)) || 0), 0);
      const contractTotal = invoice ? Number(invoice.invoice_total_gross ?? invoice.total_gross ?? 0) : 0;
      const editedAmount = prev[editedIndex]?.amount || 0;
      const otherFixedAmounts = prev.filter((_, i: number) => i < editedIndex).reduce((s, inst) => s + inst.amount, 0);
      const remaining = Math.max(0, contractTotal - paidTotal - otherFixedAmounts - editedAmount);
      const remainingCount = prev.length - editedIndex - 1;
      if (remainingCount <= 0) return prev;
      const perRemaining = Math.round((remaining / remainingCount) * 100) / 100;
      let distributed = 0;
      return prev.map((inst, i) => {
        if (i <= editedIndex) return inst;
        if (i === prev.length - 1) {
          return { ...inst, amount: Math.round((remaining - distributed) * 100) / 100 };
        }
        distributed += perRemaining;
        return { ...inst, amount: perRemaining };
      });
    });
  };

  const addRemodInst = () => {
    setRemodPlan(prev => [...prev, { installment_number: prev.length + 1, amount: 0, due_date: '', notes: '' }]);
  };

  const removeRemodInst = (index: number) => {
    setRemodPlan(prev => prev.filter((_, i) => i !== index).map((inst, i) => ({ ...inst, installment_number: i + 1 })));
  };

  const [searchFilter, setSearchFilter] = useState('');

  const filteredInstallments = installments.filter(inst => {
    if (searchFilter && !(inst.tenant_name || '').toLowerCase().includes(searchFilter.toLowerCase()) && !(inst.contract_number || '').toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  const rateSummary = useMemo(() => {
    let total = 0;
    let paid = 0;
    let pending = 0;
    let overdue = 0;
    for (const inst of filteredInstallments) {
      const amount = Number(inst.amount || 0);
      total += amount;
      if (inst.status === 'paid') paid += amount;
      else if (inst.status === 'overdue') overdue += amount;
      else pending += amount;
    }
    return { count: filteredInstallments.length, total, paid, pending, overdue };
  }, [filteredInstallments]);

  const handleExportCSV = () => {
    exportToCSV(filteredInstallments as unknown as Record<string, unknown>[], 'pagamenti', [
      { key: 'tenant_name', label: 'Cliente' },
      { key: 'installment_number', label: 'Rata' },
      { key: 'amount', label: 'Importo' },
      { key: 'due_date', label: 'Scadenza' },
      { key: 'status', label: 'Stato' },
      { key: 'contract_number', label: 'Contratto' },
      { key: 'paid_at', label: 'Pagato il' },
    ]);
  };

  if (loading) return <div className="text-muted-foreground dark:text-muted-foreground/70 p-8">Caricamento dashboard pagamenti...</div>;

  return (
    <div className="space-y-6">
      {countAdvFilters(advFilters) > 0 && (
        <div className="flex justify-end"><FilteredBadge filters={advFilters} /></div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Totale Incassato', value: dashboard?.total_collected || 0, icon: DollarSign, color: 'text-green-400' },
          { label: 'Da Incassare', value: dashboard?.total_pending || 0, icon: Clock, color: 'text-blue-400' },
          { label: 'Rate Scadute', value: dashboard?.total_overdue || 0, icon: AlertTriangle, color: 'text-red-400' },
          { label: 'Previsione 30gg', value: dashboard?.forecast_30d || 0, icon: TrendingUp, color: 'text-purple-400' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">{kpi.label}</span>
              <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
            </div>
            <p className="text-2xl font-bold text-foreground dark:text-white">€ {Number(kpi.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
          </div>
        ))}
      </div>

      {failedPayments.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="text-red-700 dark:text-red-400 font-semibold">Rate Non Andate a Buon Fine ({failedPayments.length})</h3>
          </div>
          <div className="space-y-2">
            {failedPayments.map((fp) => (
              <div key={fp.id} className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800/50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-foreground dark:text-white font-medium">{fp.tenant_name}</span>
                    <Badge className="bg-red-500/20 text-red-400">{fp.status}</Badge>
                    {fp.payment_suspended && <Badge className="bg-amber-500/20 text-amber-400">Sospeso</Badge>}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground dark:text-muted-foreground/70">
                    <span>Rata #{fp.installment_number} - € {Number(fp.amount).toFixed(2)}</span>
                    <span>Scadenza: {new Date(fp.due_date ?? '').toLocaleDateString('it-IT')}</span>
                    {fp.contract_number && <span>Contratto: {fp.contract_number}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Tip label="Ritenta addebito"><button onClick={() => retryPayment(fp.id)} className="text-blue-400 hover:text-blue-300 p-1.5 rounded-lg hover:bg-blue-500/10"><RefreshCw className="h-4 w-4" /></button></Tip>
                  {!fp.stripe_invoice_id && (
                    <Tip label="Addebita via Stripe">
                      <button onClick={() => chargeInstallment(fp.id)} disabled={chargingId === fp.id}
                        className="text-purple-400 hover:text-purple-300 p-1.5 rounded-lg hover:bg-purple-500/10 disabled:opacity-50">
                        {chargingId === fp.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                      </button>
                    </Tip>
                  )}
                  {(fp.status === 'pending' || fp.status === 'overdue' || fp.status === 'failed') && (
                    <Tip label="Registra pagamento manuale (bonifico/contanti)">
                      <button onClick={() => setManualPayModalPg({ id: fp.id, number: fp.installment_number ?? 0, amount: Number(fp.amount) })}
                        className="text-emerald-400 hover:text-emerald-300 p-1.5 rounded-lg hover:bg-emerald-500/10">
                        <Banknote className="h-4 w-4" />
                      </button>
                    </Tip>
                  )}
                  {fp.contract_id && !fp.payment_suspended && (
                    <Tip label="Sospendi pagamenti"><button onClick={() => suspendPayments(fp.contract_id ?? '', fp.contract_number ?? '')} className="text-amber-400 hover:text-amber-300 p-1.5 rounded-lg hover:bg-amber-500/10"><XCircle className="h-4 w-4" /></button></Tip>
                  )}
                  {fp.contract_id && fp.payment_suspended && (
                    <Tip label="Riprendi pagamenti"><button onClick={() => resumePayments(fp.contract_id ?? '')} className="text-green-400 hover:text-green-300 p-1.5 rounded-lg hover:bg-green-500/10"><CheckCircle className="h-4 w-4" /></button></Tip>
                  )}
                  <Tip label="Rimodula rate"><button onClick={() => openRemodulate(fp as unknown as Installment)} className="text-purple-400 hover:text-purple-300 p-1.5 rounded-lg hover:bg-purple-500/10"><Edit className="h-4 w-4" /></button></Tip>
                  {Boolean(fp.tenant_slug) && (
                    <Tip label="Vai al tenant"><a href={appPath(`/tenants/${fp.tid || fp.tenant_id}`)} className="text-muted-foreground/70 hover:text-foreground p-1.5 rounded-lg hover:bg-muted/500/10"><Link2 className="h-4 w-4" /></a></Tip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-foreground dark:text-white font-semibold mb-4">MRR - Monthly Recurring Revenue</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={mrrData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
            <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
            <Tooltip content={(tooltipProps) => {
              const { active, payload, label } = tooltipProps as unknown as { active?: boolean; payload?: { value?: number }[]; label?: string | number };
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg">
                  <p className="text-muted-foreground dark:text-muted-foreground/60 text-xs mb-1">{label}</p>
                  <p className="text-foreground dark:text-white text-sm font-semibold">MRR: € {Number(payload[0]?.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                </div>
              );
            }} />
            <Bar dataKey="mrr" fill="#3B82F6" radius={[4, 4, 0, 0]} name="MRR (€)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-foreground dark:text-white font-semibold">Rate</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
              <input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Cerca..."
                className="pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm w-44" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Tutti gli stati</option>
              <option value="pending">In Attesa</option>
              <option value="paid">Pagate</option>
              <option value="planned">Pianificate</option>
              <option value="overdue">Scadute</option>
              <option value="failed">Fallite</option>
              <option value="processing">In Elaborazione</option>
            </select>
            <AdvancedFilters filters={advFilters} onChange={setAdvFilters} dateLabel="Scadenza" />
            <button onClick={handleExportCSV} className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
              <Download className="h-4 w-4" /> CSV
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            { label: 'Rate (filtrate)', value: rateSummary.count, color: 'text-blue-400', fmt: 'num' as const },
            { label: 'Totale', value: rateSummary.total, color: 'text-foreground dark:text-white', fmt: 'eur' as const },
            { label: 'Incassato', value: rateSummary.paid, color: 'text-green-400', fmt: 'eur' as const },
            { label: 'Da Incassare', value: rateSummary.pending, color: 'text-amber-400', fmt: 'eur' as const },
            { label: 'Scaduto', value: rateSummary.overdue, color: 'text-red-400', fmt: 'eur' as const },
          ].map((s, i) => (
            <div key={i} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">{s.label}</p>
              <p className={`text-lg font-bold ${s.color}`}>
                {s.fmt === 'eur' ? `€ ${Number(s.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : s.value}
              </p>
            </div>
          ))}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border dark:border-gray-700">
              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Cliente</th>
              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Rata</th>
              <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Importo</th>
              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Scadenza</th>
              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato</th>
              <th className="text-center py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredInstallments.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground dark:text-muted-foreground/70">Nessuna rata</td></tr>
            ) : filteredInstallments.map((inst) => (
              <tr key={inst.id} className="border-b border-border dark:border-gray-700/50 hover:bg-muted dark:hover:bg-gray-700/50">
                <td className="py-2 px-3 text-foreground dark:text-white text-sm">{inst.tenant_name || inst.tenant_id}</td>
                <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">#{inst.installment_number}{inst.is_entry_fee ? ' (Entry Fee)' : ''}</td>
                <td className="py-2 px-3 text-right text-muted-foreground dark:text-muted-foreground/60 text-sm">€ {Number(inst.amount).toFixed(2)}</td>
                <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{new Date(inst.due_date || '').toLocaleDateString('it-IT')}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    <Badge className={inst.status === 'paid' ? 'bg-green-500/20 text-green-400' : inst.status === 'overdue' ? 'bg-red-500/20 text-red-400' : inst.status === 'failed' ? 'bg-red-500/20 text-red-400' : inst.status === 'processing' ? 'bg-blue-500/20 text-blue-400' : inst.status === 'planned' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'}>
                      {({ pending: 'In Attesa', paid: 'Pagata', overdue: 'Scaduta', failed: 'Fallita', processing: 'In Elaborazione', planned: 'Pianificata' } as Record<string, string>)[inst.status ?? ''] || inst.status}
                    </Badge>
                    {inst.payment_suspended && <Badge className="bg-amber-500/20 text-amber-400">Sospeso</Badge>}
                  </div>
                </td>
                <td className="py-2 px-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {inst.tenant_id && (
                      <Tip label="Dettaglio pagamenti"><button onClick={() => viewClient(inst.tenant_id ?? '')} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Eye className="h-4 w-4" /></button></Tip>
                    )}
                    {(inst.status === 'failed' || inst.status === 'overdue') && (
                      <Tip label="Ritenta"><button onClick={() => retryPayment(inst.id)} className="text-blue-400 hover:text-blue-300 p-1"><RefreshCw className="h-3 w-3" /></button></Tip>
                    )}
                    {(inst.status === 'pending' || inst.status === 'overdue' || inst.status === 'failed') && !inst.stripe_invoice_id && new Date(inst.due_date ?? '') <= new Date() && (
                      <Tip label="Addebita via Stripe">
                        <button onClick={() => chargeInstallment(inst.id)} disabled={chargingId === inst.id}
                          className="text-purple-400 hover:text-purple-300 p-1 disabled:opacity-50">
                          {chargingId === inst.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
                        </button>
                      </Tip>
                    )}
                    {(inst.status === 'pending' || inst.status === 'overdue' || inst.status === 'failed') && (
                      <Tip label="Registra pagamento manuale (bonifico/contanti)">
                        <button onClick={() => setManualPayModalPg({ id: inst.id, number: inst.installment_number ?? 0, amount: Number(inst.amount) })}
                          className="text-emerald-400 hover:text-emerald-300 p-1">
                          <Banknote className="h-3 w-3" />
                        </button>
                      </Tip>
                    )}
                    {inst.contract_id && !inst.payment_suspended && inst.status !== 'paid' && (
                      <Tip label="Sospendi pagamenti"><button onClick={() => suspendPayments(inst.contract_id ?? '', inst.contract_number ?? '')} className="text-amber-400 hover:text-amber-300 p-1"><XCircle className="h-3 w-3" /></button></Tip>
                    )}
                    {inst.contract_id && inst.payment_suspended && (
                      <Tip label="Riprendi pagamenti"><button onClick={() => resumePayments(inst.contract_id ?? '')} className="text-green-400 hover:text-green-300 p-1"><CheckCircle className="h-3 w-3" /></button></Tip>
                    )}
                    <Tip label="Rimodula"><button onClick={() => openRemodulate(inst)} className="text-purple-400 hover:text-purple-300 p-1"><Edit className="h-3 w-3" /></button></Tip>
                    {(inst.status === 'pending' || inst.status === 'planned') && !inst.is_entry_fee && !inst.stripe_invoice_id && new Date(inst.due_date ?? '') > new Date() && (
                      <Tip label="Annulla rata">
                        <button onClick={() => cancelInstallment(inst.id)} disabled={cancellingId === inst.id}
                          className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50">
                          {cancellingId === inst.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                        </button>
                      </Tip>
                    )}
                    {inst.tenant_id && (
                      <Tip label="Vai al tenant"><a href={appPath(`/tenants/${inst.tenant_id}`)} className="text-muted-foreground/70 hover:text-foreground p-1"><Link2 className="h-3 w-3" /></a></Tip>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {aging && (
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-foreground dark:text-white font-semibold mb-4">Report Aging</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Non scaduto', value: aging.not_due || 0, color: 'text-blue-400' },
              { label: '0-30gg', value: aging['0_30'] || 0, color: 'text-yellow-400' },
              { label: '31-60gg', value: aging['31_60'] || 0, color: 'text-orange-400' },
              { label: '61-90gg', value: aging['61_90'] || 0, color: 'text-red-400' },
              { label: '>90gg', value: aging.over_90 || 0, color: 'text-red-500' },
            ].map((r, i) => (
              <div key={i} className="bg-muted dark:bg-gray-700/50 rounded-lg p-4 text-center">
                <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-1">{r.label}</p>
                <p className={`text-xl font-bold ${r.color}`}>€ {Number(r.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
        <button onClick={() => setShowMonitoring(!showMonitoring)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 dark:hover:bg-gray-700/50 transition-colors">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-400" />
            <h3 className="text-foreground dark:text-white font-semibold">Cron & Monitoraggio</h3>
            {syncErrors && (syncErrors.total_errors ?? 0) > 0 && (
              <Badge className="bg-red-500/20 text-red-400">{syncErrors.total_errors} errori</Badge>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground/70 transition-transform ${showMonitoring ? 'rotate-180' : ''}`} />
        </button>
        {showMonitoring && (
          <div className="border-t border-border dark:border-gray-700 p-4 space-y-4">
            <div className="flex gap-2 border-b border-border dark:border-gray-700 pb-3">
              {(['cron', 'upcoming', 'errors', 'webhook'] as const).map(tab => (
                <button key={tab} onClick={() => setMonitoringTab(tab)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${monitoringTab === tab ? 'bg-purple-500/20 text-purple-400' : 'text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'}`}>
                  {tab === 'cron' ? 'Storico Cron' : tab === 'upcoming' ? 'Prossimi Addebiti' : tab === 'webhook' ? 'Webhook Stripe' : `Errori Sync${syncErrors?.total_errors ? ` (${syncErrors.total_errors})` : ''}`}
                </button>
              ))}
              <Tip label="Aggiorna"><button onClick={() => { void loadMonitoring(); if (monitoringTab === 'webhook') void loadWebhookTest(); if (monitoringTab === 'cron') void loadCronLogs(); }} className="ml-auto text-muted-foreground/70 hover:text-muted-foreground/60 p-1"><RefreshCw className="h-4 w-4" /></button></Tip>
            </div>

            {monitoringTab === 'cron' && (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {cronLogsLoading ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Caricamento...</p>
                ) : (cronLogs.length === 0 && cronHistory.length === 0) ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Nessuna esecuzione cron registrata</p>
                ) : [...cronLogs, ...cronHistory.filter((h) => !cronLogs.some((cl) => cl.id === h.id))].sort((a, b) => new Date(b.started_at ?? '').getTime() - new Date(a.started_at ?? '').getTime()).map((job) => (
                  <div key={job.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-muted/50 dark:bg-gray-700/50 text-sm">
                    <Badge className={job.status === 'completed' ? 'bg-green-500/20 text-green-400' : job.status === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}>
                      {job.status}
                    </Badge>
                    <span className="text-foreground dark:text-white font-medium">{job.job_type}</span>
                    <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">
                      {job.processed_count || 0} elaborati, {job.success_count || 0} ok, {job.error_count || 0} errori
                    </span>
                    {job.duration_ms && <span className="text-muted-foreground/70 text-xs">{job.duration_ms}ms</span>}
                    {job.error_message && <span className="text-red-400 text-xs truncate max-w-[200px]" title={job.error_message}>{job.error_message}</span>}
                    {Boolean(job.result_details) && (
                      <Tip label="Dettagli"><button onClick={() => { try { console.log(JSON.parse(String(job.result_details))); } catch {} }} className="text-muted-foreground/70 hover:text-foreground p-0.5"><Info className="h-3 w-3" /></button></Tip>
                    )}
                    <span className="text-muted-foreground/70 text-xs ml-auto">{job.started_at ? new Date(job.started_at).toLocaleString('it-IT') : ''}</span>
                  </div>
                ))}
              </div>
            )}

            {monitoringTab === 'upcoming' && (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {upcomingCharges.length === 0 ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Nessun addebito pianificato</p>
                ) : upcomingCharges.map((uc) => (
                  <div key={uc.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50 dark:bg-gray-700/50 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-foreground dark:text-white font-medium">{uc.tenant_name}</span>
                      <span className="text-muted-foreground dark:text-muted-foreground/70">Rata #{uc.installment_number}{uc.is_entry_fee ? ' (Entry Fee)' : ''}</span>
                      {uc.payment_suspended && <Badge className="bg-amber-500/20 text-amber-400">Sospeso</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-foreground dark:text-white font-medium">€ {Number(uc.amount).toFixed(2)}</span>
                      <span className={`text-sm ${new Date(uc.due_date ?? '') <= new Date() ? 'text-red-400 font-medium' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
                        {new Date(uc.due_date ?? '').toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {monitoringTab === 'errors' && (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {(!syncErrors || syncErrors.total_errors === 0) ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Nessun errore di sincronizzazione</p>
                ) : (
                  <>
                    {(syncErrors.stripe_errors?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-foreground dark:text-white font-medium text-sm mb-2 flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-red-400" /> Errori Stripe ({syncErrors.stripe_errors?.length ?? 0})
                        </h4>
                        <div className="space-y-1.5">
                          {syncErrors.stripe_errors?.map((err) => (
                            <div key={err.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-red-50 dark:bg-red-900/10 text-sm">
                              <div>
                                <span className="text-foreground dark:text-white">{err.tenant_name}</span>
                                <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs ml-2">Rata #{err.installment_number}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-foreground dark:text-white">€ {(Number(err.amount) / 100).toFixed(2)}</span>
                                <span className="text-red-400 text-xs truncate max-w-[200px]">{err.failure_message}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(syncErrors.fic_errors?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-foreground dark:text-white font-medium text-sm mb-2 flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-orange-400" /> Errori Fatture in Cloud ({syncErrors.fic_errors?.length ?? 0})
                        </h4>
                        <div className="space-y-1.5">
                          {syncErrors.fic_errors?.map((err) => (
                            <div key={err.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-orange-50 dark:bg-orange-900/10 text-sm">
                              <div>
                                <span className="text-foreground dark:text-white">{err.entity_type}</span>
                                <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs ml-2">{err.action}</span>
                              </div>
                              <span className="text-orange-400 text-xs truncate max-w-[250px]">{err.error_message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(syncErrors.sdi_errors?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-foreground dark:text-white font-medium text-sm mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-400" /> Errori SDI ({syncErrors.sdi_errors?.length ?? 0})
                        </h4>
                        <div className="space-y-1.5">
                          {syncErrors.sdi_errors?.map((err) => (
                            <div key={err.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-red-50 dark:bg-red-900/10 text-sm">
                              <div>
                                <span className="text-foreground dark:text-white">{err.invoice_number}</span>
                                <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs ml-2">{err.tenant_name}</span>
                              </div>
                              <Badge className="bg-red-500/20 text-red-400">{err.fic_sdi_status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {monitoringTab === 'webhook' && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {webhookLoading ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Caricamento...</p>
                ) : !webhookTest ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Nessun dato webhook</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className={`rounded-lg p-2 text-center ${webhookTest.stripe_configured ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Stripe Config</p>
                        <p className={`font-bold text-sm ${webhookTest.stripe_configured ? 'text-green-400' : 'text-red-400'}`}>{webhookTest.stripe_configured ? 'OK' : 'Mancante'}</p>
                      </div>
                      <div className={`rounded-lg p-2 text-center ${webhookTest.webhook_secret_configured ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Webhook Secret</p>
                        <p className={`font-bold text-sm ${webhookTest.webhook_secret_configured ? 'text-green-400' : 'text-red-400'}`}>{webhookTest.webhook_secret_configured ? 'OK' : 'Mancante'}</p>
                      </div>
                      <div className="bg-muted/50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Modalità</p>
                        <p className="font-bold text-sm text-foreground dark:text-white">{webhookTest.is_live_mode ? 'LIVE' : 'TEST'}</p>
                      </div>
                      <div className={`rounded-lg p-2 text-center ${webhookTest.stripe_connected ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Connesso</p>
                        <p className={`font-bold text-sm ${webhookTest.stripe_connected ? 'text-green-400' : 'text-red-400'}`}>{webhookTest.stripe_connected ? 'Si' : 'No'}</p>
                      </div>
                    </div>

                    {(webhookTest.webhook_endpoints?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-foreground dark:text-white font-medium text-sm mb-2">Endpoint Webhook su Stripe</h4>
                        <div className="space-y-2">
                          {webhookTest.webhook_endpoints?.map((ep) => (
                            <div key={ep.id} className="bg-muted/50 dark:bg-gray-700/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-foreground dark:text-white text-sm font-mono break-all">{ep.url}</span>
                                <Badge className={ep.status === 'enabled' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{ep.status}</Badge>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {ep.enabled_events?.slice(0, 10).map((ev: string) => (
                                  <span key={ev} className="text-xs bg-gray-200 dark:bg-gray-600 text-foreground dark:text-muted-foreground/60 rounded px-1.5 py-0.5">{ev}</span>
                                ))}
                                {(ep.enabled_events?.length ?? 0) > 10 && <span className="text-xs text-muted-foreground/70">+{(ep.enabled_events?.length ?? 0) - 10} altri</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {webhookTest.webhook_endpoints?.length === 0 && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-red-700 dark:text-red-300 text-sm font-medium">Nessun webhook endpoint configurato su Stripe!</p>
                        <p className="text-red-600 dark:text-red-400 text-xs mt-1">Devi configurare un webhook endpoint nella dashboard Stripe che punti al tuo server.</p>
                      </div>
                    )}

                    {(webhookTest.issues?.length ?? 0) > 0 && (
                      <div className="space-y-1">
                        {webhookTest.issues?.map((issue: string, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-xs bg-red-50 dark:bg-red-900/20 rounded p-2">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                            <span className="text-red-700 dark:text-red-300">{issue}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {(webhookTest.recent_events?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-foreground dark:text-white font-medium text-sm mb-2">Ultimi Eventi Stripe</h4>
                        <div className="space-y-1">
                          {webhookTest.recent_events?.map((ev) => (
                            <div key={ev.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50 dark:bg-gray-700/50 text-sm">
                              <div className="flex items-center gap-2">
                                <Badge className={ev.type.includes('succeeded') ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}>{ev.type}</Badge>
                                <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{ev.created}</span>
                              </div>
                              <span className="text-muted-foreground/70 text-xs">pending: {ev.pending_webhooks}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {webhookTest.stripe_api_error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-red-700 dark:text-red-300 text-sm">{webhookTest.stripe_api_error}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ModalWrapper open={!!clientDetail} onClose={() => setClientDetail(null)} title={`Dettaglio Pagamenti - ${clientDetail?.tenant_name || ''}`} wide>
        {clientDetail && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Totale Pagato</p>
                <p className="text-green-400 font-bold">€ {Number(clientDetail.total_paid || 0).toFixed(2)}</p>
              </div>
              <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">In Sospeso</p>
                <p className="text-blue-400 font-bold">€ {Number(clientDetail.total_pending || 0).toFixed(2)}</p>
              </div>
              <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Scaduto</p>
                <p className="text-red-400 font-bold">€ {Number(clientDetail.total_overdue || 0).toFixed(2)}</p>
              </div>
            </div>
            {clientDetail.payments && clientDetail.payments.length > 0 && (
              <div>
                <h4 className="text-foreground dark:text-white font-medium mb-2">Storico Pagamenti</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {clientDetail.payments.map((p, i: number) => (
                    <div key={i} className="bg-muted dark:bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-foreground dark:text-white text-sm">€ {Number(p.amount).toFixed(2)}</p>
                        <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{p.payment_method_type || 'N/A'} - {new Date(p.created_at ?? '').toLocaleDateString('it-IT')}</p>
                      </div>
                      <Badge className={p.status === 'succeeded' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{p.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!showRemodulate} onClose={() => setShowRemodulate(null)} title="Rimodula Rate" wide>
        <div className="space-y-4">
          {showRemodulate && (
            <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 text-sm">
              <span className="text-muted-foreground dark:text-muted-foreground/70">Cliente:</span> <span className="text-foreground dark:text-white font-medium">{showRemodulate.tenant_name}</span>
              {showRemodulate.contract_number || showRemodulate.contract_id ? (
                <>
                  <span className="text-muted-foreground dark:text-muted-foreground/70 ml-4">Contratto:</span> <span className="text-foreground dark:text-white">{showRemodulate.contract_number || showRemodulate.contract_id}</span>
                </>
              ) : showRemodulate.invoice_number || showRemodulate.invoice_id ? (
                <>
                  <span className="text-muted-foreground dark:text-muted-foreground/70 ml-4">Fattura:</span> <span className="text-foreground dark:text-white">{showRemodulate.invoice_number || showRemodulate.invoice_id}</span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground dark:text-muted-foreground/70 ml-4">Pipeline:</span> <span className="text-foreground dark:text-white">{showRemodulate.pipeline_entry_id}</span>
                </>
              )}
            </div>
          )}
          <div className="space-y-2">
            {remodPlan.map((inst, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm w-8 text-center">#{inst.installment_number}</span>
                <input type="date" value={inst.due_date}
                  onChange={(e) => updateRemodInst(i, 'due_date', e.target.value)}
                  className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1" />
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">€</span>
                  <input type="number" step="0.01" value={inst.amount}
                    onChange={(e) => updateRemodInst(i, 'amount', e.target.value)}
                    className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm w-28" />
                </div>
                {i < remodPlan.length - 1 && (
                  <Tip label="Ricalcola rate successive"><button onClick={() => recalculateRemaining(i)} className="text-cyan-400 hover:text-cyan-300 p-1"><RefreshCw className="h-3 w-3" /></button></Tip>
                )}
                <input type="text" value={inst.notes} placeholder="Note..."
                  onChange={(e) => updateRemodInst(i, 'notes', e.target.value)}
                  className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1" />
                {remodPlan.length > 1 && (
                  <button onClick={() => removeRemodInst(i)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-3 w-3" /></button>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <button onClick={addRemodInst} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi Rata</button>
            <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Totale: € {remodPlan.reduce((s, i) => s + i.amount, 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRemodulate(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={saveRemodulation} disabled={remodSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
              {remodSaving ? 'Salvataggio...' : 'Salva Rimodulazione'}
            </button>
          </div>
        </div>
      </ModalWrapper>

      {manualPayModalPg && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setManualPayModalPg(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-foreground dark:text-white font-semibold text-lg mb-1">Registra Pagamento Manuale</h3>
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Rata #{manualPayModalPg.number} — € {manualPayModalPg.amount.toFixed(2)}</p>
            <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Metodo di pagamento *</label>
            <select value={manualPayMethodPg} onChange={e => setManualPayMethodPg(e.target.value as 'bank_transfer' | 'cash' | 'cheque')}
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-3">
              <option value="bank_transfer">Bonifico bancario</option>
              <option value="cash">Contanti</option>
              <option value="cheque">Assegno</option>
            </select>
            <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Riferimento bancario (CRO / IBAN mittente)</label>
            <input type="text" value={manualPayRefPg} onChange={e => setManualPayRefPg(e.target.value)} placeholder="Es. CRO 1234567890 o IBAN IT..."
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-3" />
            <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Note</label>
            <textarea value={manualPayNotesPg} onChange={e => setManualPayNotesPg(e.target.value)} rows={2} placeholder="Note opzionali..."
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setManualPayModalPg(null); setManualPayMethodPg('bank_transfer'); setManualPayRefPg(''); setManualPayNotesPg(''); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-foreground dark:text-foreground rounded-lg text-sm">Annulla</button>
              <button onClick={handleManualPaymentPg} disabled={manualPayLoadingPg}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {manualPayLoadingPg ? 'Registrazione...' : 'Conferma Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {globalSuspendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-foreground dark:text-white mb-2">Sospendi Pagamenti</h3>
            {globalSuspendModal.contractNumber && (
              <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Contratto: {globalSuspendModal.contractNumber}</p>
            )}
            <label className="text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1 block">Motivo della sospensione *</label>
            <textarea value={globalSuspendReason} onChange={e => setGlobalSuspendReason(e.target.value)} rows={3} placeholder="Inserisci il motivo della sospensione..."
              className="w-full border border-border dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-foreground dark:text-white mb-4" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setGlobalSuspendModal(null)} className="px-4 py-2 text-sm text-muted-foreground dark:text-muted-foreground/60 hover:bg-muted dark:hover:bg-gray-700 rounded-lg">Annulla</button>
              <button onClick={handleGlobalSuspend} disabled={!globalSuspendReason.trim() || globalSuspendLoading}
                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50">
                {globalSuspendLoading ? 'Sospensione...' : 'Sospendi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

